import { useState, type FormEvent } from 'react';
import { useAuth } from '@/auth/AuthContext';

const SIGNUP_URL = 'https://app.libertify.com';

type Tab = 'password' | 'token';

/**
 * Locked overlay shown until the user authenticates. Once signed in, this
 * component just renders its children.
 *
 * Two tabs:
 *  - **Email & password** — calls `POST /api/tokens` (the same path the
 *    Libertify web app uses). Works for every account, including those
 *    provisioned via Google/Apple SSO.
 *  - **Paste access token** — power-user fallback for developers who
 *    already have a token (from the desktop app, Chrome extension, AWS CLI,
 *    or their own backend).
 */
export function SignInGate({ children }: { children: React.ReactNode }) {
  const { tokens, signIn, signInWithToken, error, loading } = useAuth();
  const [tab, setTab] = useState<Tab>('password');

  if (tokens) return <>{children}</>;

  return (
    <div className="lp-flex lp-items-start lp-justify-center lp-py-2">
      <div
        className="lp-w-full lp-max-w-md lp-shadow-card"
        style={{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 22,
          padding: 32,
        }}
      >
        <div className="lp-text-center lp-mb-6">
          <div
            className="lp-w-12 lp-h-12 lp-mx-auto lp-flex lp-items-center lp-justify-center lp-mb-4"
            style={{
              borderRadius: 14,
              background: 'linear-gradient(135deg, #f1f0ff, #e4e0ff)',
              color: '#4f3ff3',
            }}
          >
            <svg className="lp-w-5 lp-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c0-1.105.895-2 2-2s2 .895 2 2-.895 2-2 2-2-.895-2-2zM7 11V7a5 5 0 0110 0v4" />
              <rect x="5" y="11" width="14" height="10" rx="2" />
            </svg>
          </div>
          <h2 className="lp-text-xl lp-font-bold lp-tracking-tight" style={{ color: '#0f172a' }}>
            Sign in to use the playground
          </h2>
          <p className="lp-text-[13px] lp-mt-1.5" style={{ color: '#64748b' }}>
            We&apos;ll only use your token to call the Libertify API from your browser. WordPress never sees it.
          </p>
        </div>

        <div
          className="lp-inline-flex lp-w-full lp-p-0.5 lp-mb-5 lp-gap-0.5"
          style={{ background: '#f1f5f9', borderRadius: 10 }}
        >
          <TabButton active={tab === 'password'} onClick={() => setTab('password')}>
            Email &amp; password
          </TabButton>
          <TabButton active={tab === 'token'} onClick={() => setTab('token')}>
            Paste access token
          </TabButton>
        </div>

        {tab === 'password' ? (
          <PasswordForm onSignIn={signIn} loading={loading} error={error} />
        ) : (
          <TokenForm onSignIn={signInWithToken} />
        )}

        <div
          className="lp-mt-6 lp-pt-4 lp-text-[12px] lp-text-center"
          style={{ borderTop: '1px solid #f1f5f9', color: '#64748b' }}
        >
          No Libertify account yet?{' '}
          <a
            href={SIGNUP_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="lp-font-semibold hover:lp-underline"
            style={{ color: '#6256ff' }}
          >
            Create one →
          </a>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="lp-flex-1 lp-text-xs lp-font-semibold lp-px-3 lp-py-2 lp-transition-all"
      style={{
        borderRadius: 8,
        background: active ? '#ffffff' : 'transparent',
        color: active ? '#0f172a' : '#64748b',
        boxShadow: active ? '0 1px 2px rgba(15,23,42,0.06)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

// =============================================================================
// Email + password form (POST /api/tokens)
// =============================================================================

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: '10px 12px',
  fontSize: 14,
  color: '#0f172a',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  color: '#64748b',
  letterSpacing: '0.08em',
};

function PasswordForm({
  onSignIn,
  loading,
  error,
}: {
  onSignIn: (email: string, password: string) => Promise<void>;
  loading: boolean;
  error: { type: string; message: string } | null;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    try {
      await onSignIn(email, password);
    } catch {
      /* surfaced via `error` */
    }
  };

  return (
    <form onSubmit={onSubmit} className="lp-space-y-3">
      <label className="lp-block">
        <span style={labelStyle}>Email</span>
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          required
          className="lp-mt-1 lp-focus-ring"
          style={inputStyle}
        />
      </label>
      <label className="lp-block">
        <span style={labelStyle}>Password</span>
        <div className="lp-mt-1 lp-relative">
          <input
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="lp-focus-ring"
            style={{ ...inputStyle, paddingRight: 70 }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="lp-absolute lp-text-xs lp-font-medium lp-px-2 lp-py-1 lp-rounded"
            style={{ right: 6, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}
            tabIndex={-1}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
      </label>

      {error && <ErrorBanner type={error.type} message={error.message} />}

      <button
        type="submit"
        disabled={loading || !email || !password}
        className="lp-btn-primary lp-w-full lp-py-2.5 lp-px-4 lp-text-sm lp-font-bold lp-flex lp-items-center lp-justify-center lp-gap-2"
        style={{ borderRadius: 12 }}
      >
        {loading && (
          <svg className="lp-w-4 lp-h-4 lp-animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="lp-opacity-25" />
            <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        )}
        Sign in
      </button>

      <p className="lp-text-[11px] lp-text-center lp-pt-1" style={{ color: '#94a3b8' }}>
        Forgot your password?{' '}
        <a
          href={SIGNUP_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="lp-font-medium hover:lp-underline"
          style={{ color: '#64748b' }}
        >
          Reset it on app.libertify.com
        </a>
      </p>
    </form>
  );
}

// =============================================================================
// Paste-token form
// =============================================================================

function TokenForm({
  onSignIn,
}: {
  onSignIn: (accessToken: string, opts?: { idToken?: string; refreshToken?: string }) => void;
}) {
  const [accessToken, setAccessToken] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [idToken, setIdToken] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const trimmed = accessToken.trim();
    if (!trimmed) {
      setLocalError('Paste an access token to continue.');
      return;
    }
    if (!isLikelyJwt(trimmed)) {
      setLocalError('That doesn\'t look like a JWT (expected three dot-separated parts).');
      return;
    }
    onSignIn(trimmed, { idToken: idToken.trim() || undefined });
  };

  return (
    <form onSubmit={submit} className="lp-space-y-3">
      <div
        className="lp-text-[12px] lp-px-3 lp-py-2.5 lp-leading-relaxed"
        style={{
          background: '#f8f7ff',
          border: '1px solid #cbc4ff',
          borderRadius: 10,
          color: '#334155',
        }}
      >
        <span className="lp-font-semibold" style={{ color: '#0f172a' }}>Bring your own token.</span>{' '}
        Paste a Libertify <code style={codeStyle}>access_token</code> — the playground will use it as the{' '}
        <code style={codeStyle}>Authorization: Bearer …</code> header for live requests.
      </div>

      <label className="lp-block">
        <span style={labelStyle}>Access token</span>
        <textarea
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          placeholder="eyJraWQiOi…"
          rows={4}
          spellCheck={false}
          className="lp-mt-1 lp-focus-ring lp-font-mono"
          style={{ ...inputStyle, fontSize: 11.5, lineHeight: '1.4', resize: 'vertical' }}
        />
      </label>

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="lp-text-[11.5px] lp-font-semibold"
        style={{ color: '#6256ff' }}
      >
        {showAdvanced ? '− Hide' : '+ Show'} optional id_token
      </button>

      {showAdvanced && (
        <label className="lp-block">
          <span style={labelStyle}>
            ID token <span style={{ color: '#94a3b8', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(used to display your email)</span>
          </span>
          <textarea
            value={idToken}
            onChange={(e) => setIdToken(e.target.value)}
            placeholder="eyJraWQi…"
            rows={3}
            spellCheck={false}
            className="lp-mt-1 lp-focus-ring lp-font-mono"
            style={{ ...inputStyle, fontSize: 11.5, lineHeight: '1.4', resize: 'vertical' }}
          />
        </label>
      )}

      {localError && (
        <div
          className="lp-text-[12px] lp-px-3 lp-py-2"
          style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', borderRadius: 10 }}
        >
          {localError}
        </div>
      )}

      <button
        type="submit"
        disabled={!accessToken.trim()}
        className="lp-btn-primary lp-w-full lp-py-2.5 lp-px-4 lp-text-sm lp-font-bold"
        style={{ borderRadius: 12 }}
      >
        Use this token
      </button>

      <details className="lp-text-[11.5px]" style={{ color: '#64748b' }}>
        <summary className="lp-cursor-pointer lp-font-semibold" style={{ color: '#475569' }}>
          How do I get an access token?
        </summary>
        <ul className="lp-mt-2 lp-space-y-1.5 lp-pl-4 lp-list-disc" style={{ color: '#64748b' }}>
          <li>
            <strong>Desktop app:</strong> open DevTools, run{' '}
            <code style={codeStyle}>localStorage.getItem(&apos;cognitoTokens&apos;)</code> in the console.
          </li>
          <li>
            <strong>Chrome extension:</strong> open the background-page DevTools, then{' '}
            <code style={codeStyle}>chrome.storage.local.get(&apos;cognitoTokens&apos;, console.log)</code>.
          </li>
          <li>
            <strong>Backend / cURL:</strong>{' '}
            <code style={codeStyle}>curl -d &quot;username=…&amp;password=…&quot; https://api.libertify.com/api/tokens</code>
            {' '}— grab the <code style={codeStyle}>access_token</code> field.
          </li>
        </ul>
      </details>
    </form>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function ErrorBanner({ type, message }: { type: string; message: string }) {
  return (
    <div
      className="lp-text-[12.5px] lp-px-3 lp-py-2.5"
      style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', borderRadius: 10 }}
    >
      <div className="lp-font-semibold">{friendlyTitle(type)}</div>
      <div className="lp-text-[11.5px] lp-mt-0.5">{message}</div>
    </div>
  );
}

const codeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  padding: '1px 5px',
  borderRadius: 4,
  fontSize: 11.5,
};

function isLikelyJwt(s: string): boolean {
  return s.split('.').length === 3 && s.length > 50;
}

function friendlyTitle(type: string): string {
  switch (type) {
    case 'InvalidCredentials':
      return 'Incorrect email or password';
    case 'MissingCredentials':
      return 'Please fill both fields';
    case 'TooManyRequests':
      return 'Too many attempts';
    case 'NetworkError':
      return 'Network error';
    case 'ServerError':
      return 'Libertify Auth is having a moment';
    default:
      return 'Sign-in failed';
  }
}
